import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import ScrollPickerSelector from './ScrollPickerSelector';

/**
 * Shared timeframe control (scroll-picker) used across Market Overview,
 * Performance, Relative Strength, and Account views.
 *
 * @param {string} value
 * @param {(next: string) => void} onChange
 * @param {Array<{value:string,label:string}|string>} options
 * @param {'compact'|'default'|'emphasis'} [size='default']
 * @param {string} [ariaLabel='Timeframe']
 * @param {string} [testId]
 * @param {number|string} [width] - override width
 * @param {number|string} [minWidth]
 */
export default function TimeframeSelect({
  value,
  onChange,
  options = [],
  size = 'default',
  ariaLabel = 'Timeframe',
  testId,
  width,
  minWidth,
}) {
  const { colors } = useTheme();
  const normalized = useMemo(() => (
    (options || []).map((o) => (
      typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label || o.value }
    ))
  ), [options]);

  const sizeStyles = {
    compact: { width: 96, minWidth: 88, height: 28, fontSize: '0.8rem', fontWeight: 600 },
    default: { width: 128, minWidth: 110, height: 32, fontSize: '0.85rem', fontWeight: 600 },
    emphasis: { width: 148, minWidth: 140, height: 40, fontSize: '1rem', fontWeight: 700 },
  };
  const s = sizeStyles[size] || sizeStyles.default;
  const selected = normalized.find((o) => String(o.value) === String(value)) || normalized[0] || null;

  return (
    <div
      data-testid={testId}
      style={{
        flexShrink: 0,
        overflow: 'visible',
        width: width ?? s.width,
        minWidth: minWidth ?? s.minWidth,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <ScrollPickerSelector
        items={normalized}
        value={selected}
        onChange={(item) => {
          if (item && item.value != null) onChange?.(item.value);
        }}
        getItemKey={(item) => item.value}
        getItemLabel={(item) => item.label}
        placeholder={ariaLabel}
        ariaLabel={ariaLabel}
        testId={testId ? `${testId}-picker` : undefined}
        showDropdown
        buttonStyle={{
          width: '100%',
          minHeight: s.height,
          height: s.height,
          padding: '0 10px',
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.bgInput || colors.bgSecondary || colors.bgCard || 'transparent',
          justifyContent: 'center',
          fontSize: s.fontSize,
          fontWeight: s.fontWeight,
        }}
        renderItem={(item, isSelected) => (
          <span
            style={{
              fontSize: 18,
              fontWeight: isSelected ? 700 : 500,
              color: isSelected ? colors.primary : colors.text,
              textShadow: isSelected ? `0 0 10px ${colors.primary}40` : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            {item.label}
          </span>
        )}
      />
    </div>
  );
}

/**
 * Horizontal timeframe chips — preferred on Market Overview (no dropdown clipping).
 */
export function TimeframeChips({
  value,
  onChange,
  options = [],
  testId,
  ariaLabel = 'Timeframe',
}) {
  const { colors } = useTheme();
  const normalized = (options || []).map((o) => (
    typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label || o.value }
  ));

  return (
    <div
      data-testid={testId}
      role="group"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        flexWrap: 'nowrap',
        gap: 4,
        minWidth: 0,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        paddingBottom: 1,
      }}
    >
      {normalized.map((opt) => {
        const active = String(opt.value) === String(value);
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange?.(opt.value)}
            style={{
              flex: '0 0 auto',
              height: 28,
              padding: '0 10px',
              borderRadius: 8,
              border: `1px solid ${active ? colors.primary : colors.border}`,
              backgroundColor: active ? `${colors.primary}22` : (colors.bgSecondary || colors.bgCard),
              color: active ? colors.primary : colors.textMuted,
              fontSize: 12,
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              lineHeight: '26px',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Field label stacked above a control (ticker / list / timeframe rows).
 */
export function FieldControl({ label, children, colors: colorsProp, style, labelTestId }) {
  const theme = useTheme();
  const colors = colorsProp || theme.colors;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 4,
        minWidth: 0,
        ...style,
      }}
    >
      {label ? (
        <span
          data-testid={labelTestId}
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: colors.textMuted,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', minHeight: 28, overflow: 'visible' }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Section title with an optional trailing control (e.g. timeframe) on the same row.
 */
export function SectionTitleWithControl({
  title,
  trailing = null,
  titleTestId,
  style,
  titleStyle,
  marginBottom = 10,
}) {
  const { colors } = useTheme();
  return (
    <div
      data-testid={titleTestId}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom,
        ...style,
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: colors.text,
          flexShrink: 0,
          ...titleStyle,
        }}
      >
        {title}
      </div>
      {trailing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', minWidth: 0 }}>
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
